import type { HeaderMfeOptions } from "@/types/HeaderMfe";

type Read = (key: string) => string | null;
type AuthToken = NonNullable<HeaderMfeOptions["authToken"]>;
type ActiveEntity = NonNullable<HeaderMfeOptions["activeEntity"]>;

// legacy: no SSO key, mount as before.
// generic: an SSO key exists but the pair is unusable, so mount the
// non-integrated header exactly as today's mount without handshake data. Today's
// mount with data would send isUserIntegrated: true and no credential, which
// shows the failed-auth toast and modal. It also drops clientId: the header
// exchanges a ?code= found in the URL only when it has one, and index.asp does
// not strip a ?code= in the /sso flow.
// sso: both keys are valid, mount with the user's own token and entity, also
// without clientId. With a valid user cookie the header ignores a ?code=, but a
// token whose cookie dies before the header reads it (the header writes the
// cookie after the remote loads) would leave it to exchange a stray one.
export type DpSpacesSsoMount =
  | { mode: "legacy"; options?: undefined }
  | {
      mode: "generic";
      options: {
        isUserIntegrated: false;
        clientId: undefined;
        userIdentity: undefined;
      };
    }
  | {
      mode: "sso";
      options: {
        authToken: AuthToken;
        activeEntity: ActiveEntity;
        clientId: undefined;
      };
    };

const TOKEN_KEY = "dpSpacesAuthToken";
const ENTITY_KEY = "dpSpacesActiveEntity";
// The header's cookie for the anonymous token (header repo,
// @remotes/header/src/constants/auth.ts: ANONYMOUS_TOKEN_COOKIE).
const ANONYMOUS_TOKEN_COOKIE = "DP_REMOTES_ANONYMOUS_AUTH_TEMP";
// The header drops its token cookie at expiresAt - 300 s (header repo,
// @remotes/header/src/constants/auth.ts: TOKEN_EXPIRATION_THRESHOLD).
const HEADER_DROP_MARGIN_SECONDS = 300;
// expiresAt is epoch seconds. A value this large is epoch milliseconds, and the
// header would keep the cookie for centuries.
const MAX_EPOCH_SECONDS = 1e11;

function readRaw(read: Read, key: string): string | null {
  try {
    return read(key);
  } catch {
    // sessionStorage unavailable counts as no key, like safeReadCodeDpSpaces
    return null;
  }
}

function parseJson(raw: string | null): unknown {
  try {
    return raw === null ? undefined : JSON.parse(raw);
  } catch {
    // malformed value: the pair is unusable
    return undefined;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isEntityType(value: unknown): value is ActiveEntity["type"] {
  return value === "doctor" || value === "facility";
}

function toAuthToken(
  value: unknown,
  nowSeconds: number
): AuthToken | undefined {
  if (!isRecord(value)) return undefined;
  const { accessToken, expiresAt } = value;
  if (typeof accessToken !== "string" || !accessToken.trim()) return undefined;
  if (typeof expiresAt !== "number" || !Number.isFinite(expiresAt)) {
    return undefined;
  }
  const isMillis = expiresAt >= MAX_EPOCH_SECONDS;
  const isExpiring = expiresAt - HEADER_DROP_MARGIN_SECONDS <= nowSeconds;
  return isMillis || isExpiring ? undefined : { accessToken, expiresAt };
}

function toActiveEntity(value: unknown): ActiveEntity | undefined {
  if (!isRecord(value)) return undefined;
  const { type, id } = value;
  if (!isEntityType(type)) return undefined;
  if (typeof id !== "number" || !Number.isSafeInteger(id) || id <= 0) {
    return undefined;
  }
  return { type, id: String(id) };
}

// feegowclinic's index.asp writes both keys for an /sso login with a usable
// token and removes them on every other render, so a key that is still here
// belongs to an SSO render. The SSO mount also needs that render's handshake
// data with its userIdentity: the header takes its user id from it, and
// index.asp drops the data on every non-200 handshake.
export function resolveDpSpacesSsoMount(
  read: Read,
  nowSeconds: number,
  authenticationData: { userIdentity?: unknown } | null | undefined
): DpSpacesSsoMount {
  const rawToken = readRaw(read, TOKEN_KEY);
  const rawEntity = readRaw(read, ENTITY_KEY);
  if (rawToken === null && rawEntity === null) return { mode: "legacy" };

  const authToken = toAuthToken(parseJson(rawToken), nowSeconds);
  const activeEntity = toActiveEntity(parseJson(rawEntity));
  if (authToken && activeEntity && authenticationData?.userIdentity) {
    return {
      mode: "sso",
      options: { authToken, activeEntity, clientId: undefined },
    };
  }
  return {
    mode: "generic",
    options: {
      isUserIntegrated: false,
      clientId: undefined,
      userIdentity: undefined,
    },
  };
}

// The options handed to the header for this mount. The mount's options land
// after the handshake values in base, which is what lets generic switch off
// isUserIntegrated, clientId and userIdentity. Only legacy carries an authCode,
// and only for an integrated user, as before CON-1849.
export function buildHeaderOptions(
  base: HeaderMfeOptions,
  mount: DpSpacesSsoMount,
  codeDpSpaces: string | undefined
): HeaderMfeOptions {
  const legacyOptions =
    codeDpSpaces && base.isUserIntegrated ? { authCode: codeDpSpaces } : {};
  return { ...base, ...(mount.options ?? legacyOptions) };
}

// The header reads an anonymous token cookie next to a user token as a failed
// authentication (toast, and the Visibility click opens the failed-auth modal)
// and removes it only on its authCode path, which the SSO mount never takes. A
// cookie left over from a generic mount, a click after the user cookie lapsed
// or a 401 would turn a valid SSO mount into that state, so the SSO mount drops
// it. Name, path and attributes are the ones the header sets it with.
export function expireStaleAnonymousToken(
  mount: DpSpacesSsoMount,
  doc: { cookie: string }
): void {
  if (mount.mode !== "sso") return;
  try {
    doc.cookie = `${ANONYMOUS_TOKEN_COOKIE}=; Max-Age=0; path=/; Secure; SameSite=None`;
  } catch {
    // cookies unavailable: the header cannot have stored the cookie either
  }
}
